// CB:BEGIN fr-014-realtor-landing
import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useProgressiveDataService } from '../lib/progressiveDataService';
import { getApiBaseUrl } from '../lib/getApiUrl';
import SharedHeaderRealtor from '../components/SharedHeaderRealtor';
import ContentLimiter from '../components/layout/ContentLimiter';
import { useDevice } from '../hooks/useDevice';
import {
  ArrowRight,
  FileText,
  Home,
  CheckCircle2,
  Share2,
  ShieldCheck,
} from 'lucide-react';

const BRAND_GREEN = '#123c3b';

const RealtorLanding: React.FC<{ onNavigate: (screen: string) => void }> = ({ onNavigate }) => {
  const { isMobileLayout } = useDevice();
  const { user, isSignedIn } = useUser();
  const { processSignup } = useProgressiveDataService();
  const processedRef = useRef(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const maybeProcessSignup = async () => {
      if (processedRef.current) return;
      if (isSignedIn && user) {
        processedRef.current = true;
        try {
          const baseUrl = getApiBaseUrl();
          const token = await (window as any).Clerk?.session?.getToken?.() ?? null;
          const resp = await fetch(`${baseUrl.replace(/\/$/, '')}/api/me`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data?.role === 'REALTOR') {
              onNavigate('realtor-dashboard');
              return;
            }
          }
          await processSignup({
            user_id: user.id,
            email: user.emailAddresses?.[0]?.emailAddress || '',
            account_type: 'REALTOR',
          });
          localStorage.setItem('cb.usertype', 'realtor');
          onNavigate('realtor-dashboard');
        } catch (e) {
          console.error('Realtor processSignup failed', e);
        }
      }
    };
    maybeProcessSignup();
  }, [isSignedIn, user, processSignup, onNavigate]);

  const heroFeatureCards = [
    {
      icon: <Home size={48} style={{ color: '#fbbf24' }} />,
      title: 'Submit Any Address',
      desc: 'Enter a property address and get up to 3 contractor bids for roof replacement—no homeowner account needed.',
    },
    {
      icon: <FileText size={48} style={{ color: '#60a5fa' }} />,
      title: 'Client-Ready PDF',
      desc: 'Download a clean, branded PDF to share with your buyer or seller. Professional and easy to forward.',
    },
    {
      icon: <Share2 size={48} style={{ color: '#34d399' }} />,
      title: 'Co-Branded Portal',
      desc: 'Send a single link. Your client views bids, signs in as homeowner, and accepts one—project created in one step.',
    },
  ];

  const howItWorks = [
    {
      num: '01',
      icon: <Home size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Create Your Realtor Account',
      desc: 'Sign up from the Realtor flow. We set your role so you get access to the Realtor dashboard and quote tools.',
    },
    {
      num: '02',
      icon: <FileText size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Request Quotes for a Property',
      desc: 'Submit the property address. We match with contractors and collect up to 3 bids—idempotent so duplicate addresses within 10 minutes return the same request.',
    },
    {
      num: '03',
      icon: <CheckCircle2 size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Download PDF or Create Portal Link',
      desc: 'Download a client-ready PDF of all bids, or create a one-time Client Portal link. Your branding appears so the client knows it’s from you.',
    },
    {
      num: '04',
      icon: <ShieldCheck size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Client Accepts → Project Created',
      desc: 'Your client opens the link, signs in as homeowner, and accepts a bid. We create the CoreBids project and attribute the conversion to you.',
    },
  ];

  const realtorTestimonials = [
    {
      quote: '"My clients love getting real bids in one place. The PDF and portal link make me look prepared and professional."',
      name: 'Jennifer M.',
      company: 'Keller Williams',
    },
    {
      quote: '"Finally—roof quotes without my clients having to chase contractors. CoreBids for Realtors saves everyone time."',
      name: 'Marcus T.',
      company: 'RE/MAX Premier',
    },
    {
      quote: '"The co-branded portal builds trust. My buyers see it’s from me and CoreBids, and they can accept a bid in one click."',
      name: 'Sarah L.',
      company: 'Coldwell Banker',
    },
  ];

  const handlePrimaryCta = () => {
    window.location.href = '/sign-up?redirect_url=/realtor';
  };
  const handleBackHome = () => onNavigate('landing');

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif', background: '#fff' }}>
      <style>{`
        .cb-realtor-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,0.25); }
        .cb-realtor-btn-secondary:hover { background-color: rgba(255,255,255,0.10); border-color: rgba(255,255,255,0.50); }
        .cb-realtor-feature-card:hover { background-color: rgba(255,255,255,0.25); transform: translateY(-4px); }
        .cb-realtor-step-card:hover { border-color: ${BRAND_GREEN}; transform: translateY(-4px); box-shadow: 0 12px 32px rgba(18,60,59,0.10); }
      `}</style>

      <SharedHeaderRealtor onNavigate={onNavigate} />

      <section
        style={{
          position: 'relative',
          padding: isMobileLayout ? '56px 16px 64px' : '100px 40px 120px',
          background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 50%, #1a6b69 100%)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 80%, rgba(230, 245, 239, 0.10) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(230, 245, 239, 0.10) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        <ContentLimiter>
          <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 1000, margin: '0 auto' }}>
            <h1
              style={{
                fontSize: isMobileLayout ? 40 : 'clamp(48px, 6vw, 72px)',
                fontWeight: 900,
                color: '#fff',
                marginBottom: 24,
                lineHeight: 1.1,
                textShadow: '0 4px 20px rgba(0,0,0,0.2)',
                letterSpacing: '-0.02em',
              }}
            >
              Welcome,{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #e6f5ef, #a7f3d0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Realtors!
              </span>
            </h1>

            <p
              style={{
                fontSize: isMobileLayout ? 18 : 24,
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.6,
                maxWidth: 700,
                margin: '0 auto 48px',
              }}
            >
              Get roof replacement quotes for your clients. Share a PDF or a co-branded portal link—your client accepts a bid and we create the project. You get full attribution.
            </p>

            <div
              style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: isMobileLayout ? 40 : 64,
                flexWrap: 'wrap',
              }}
            >
              <button
                className="cb-realtor-btn-primary"
                onClick={handlePrimaryCta}
                style={{
                  fontSize: isMobileLayout ? 18 : 20,
                  fontWeight: 700,
                  padding: isMobileLayout ? '16px 22px' : '20px 48px',
                  backgroundColor: '#fff',
                  color: BRAND_GREEN,
                  border: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 44,
                }}
              >
                Get Started Free <ArrowRight size={20} />
              </button>

              <button
                className="cb-realtor-btn-secondary"
                onClick={handleBackHome}
                style={{
                  fontSize: isMobileLayout ? 16 : 18,
                  fontWeight: 600,
                  padding: isMobileLayout ? '14px 18px' : '18px 40px',
                  backgroundColor: 'transparent',
                  color: '#fff',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: 44,
                }}
              >
                ← Back to Home
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 24,
                maxWidth: 1000,
                margin: '0 auto',
              }}
            >
              {heroFeatureCards.map((c) => (
                <div
                  key={c.title}
                  className="cb-realtor-feature-card"
                  style={{
                    padding: '32px 24px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: 20,
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      padding: 16,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 16,
                      marginBottom: 20,
                    }}
                    aria-hidden="true"
                  >
                    {c.icon}
                  </div>
                  <h3 style={{ fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 12 }}>{c.title}</h3>
                  <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', backgroundColor: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#0f172a', textAlign: 'center' }}>
              How It Works
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                margin: '16px auto 64px',
                maxWidth: 600,
              }}
            >
              Four steps from signup to client conversion
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 32,
              }}
            >
              {howItWorks.map((s) => (
                <div
                  key={s.num}
                  className="cb-realtor-step-card"
                  style={{
                    display: 'flex',
                    gap: 24,
                    padding: 32,
                    backgroundColor: '#f8fafc',
                    borderRadius: 20,
                    border: '2px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div
                    style={{
                      fontSize: 48,
                      fontWeight: 900,
                      color: '#e6f5ef',
                      lineHeight: 1,
                      minWidth: 60,
                    }}
                    aria-hidden="true"
                  >
                    {s.num}
                  </div>
                  <div>
                    <div style={{ marginBottom: 12 }} aria-hidden="true">
                      {s.icon}
                    </div>
                    <h3 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>{s.title}</h3>
                    <p style={{ fontSize: 16, color: '#64748b', lineHeight: 1.6 }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      <section style={{ padding: isMobileLayout ? '56px 16px' : '80px 40px', backgroundColor: '#e6f5ef' }}>
        <ContentLimiter>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 40,
              maxWidth: 1000,
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            {[
              { big: 'Up to 3', label: 'Bids per request' },
              { big: 'Free', label: 'Realtor account' },
              { big: '1-Click', label: 'Client accept' },
            ].map((x) => (
              <div key={x.label}>
                <div style={{ fontSize: isMobileLayout ? 52 : 64, fontWeight: 900, color: BRAND_GREEN, marginBottom: 8 }}>
                  {x.big}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#334155' }}>{x.label}</div>
              </div>
            ))}
          </div>
        </ContentLimiter>
      </section>

      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', backgroundColor: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 64 }}>
              What Realtors Say
            </h2>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 24,
              }}
            >
              {realtorTestimonials.map((t) => (
                <div
                  key={t.name}
                  style={{
                    padding: 32,
                    backgroundColor: '#fff',
                    borderRadius: 20,
                    border: '2px solid #e2e8f0',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                >
                  <div style={{ fontSize: 24, color: '#fbbf24', marginBottom: 16 }} aria-hidden="true">
                    ★★★★★
                  </div>
                  <p style={{ fontSize: 15, fontStyle: 'italic', color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>
                    {t.quote}
                  </p>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: BRAND_GREEN }}>{t.name}</div>
                    <div style={{ fontSize: 14, color: '#64748b' }}>{t.company}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      <section id="realtor-faq" style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', backgroundColor: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 16 }}>
              Realtor FAQs
            </h2>
            <p style={{ fontSize: 18, color: '#64748b', textAlign: 'center', marginBottom: 64, maxWidth: 600, margin: '16px auto 64px' }}>
              Everything you need to know before requesting your first quote.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                {
                  q: 'How do I get roof quotes for my client’s property?',
                  a: 'Sign up as a Realtor, then go to your Realtor Dashboard. Enter the property address and submit. We match with contractors and collect up to 3 bids. You can download a client-ready PDF or create a one-time Client Portal link to send to your buyer or seller.',
                },
                {
                  q: 'Does my client need a CoreBids account before I request quotes?',
                  a: 'No. You request quotes with just the address. When you send the Client Portal link, your client signs in (or signs up) as a homeowner at that point and can accept a bid. We create their homeowner record and the project in one step.',
                },
                {
                  q: 'What is the Client Portal link?',
                  a: 'It’s a single-use, time-limited link that shows the bid comparison with your branding (“Presented by [You/Your Brokerage]”). Your client opens it, signs in as homeowner, and clicks Accept on one bid. We then create the CoreBids project and attribute the conversion to you.',
                },
                {
                  q: 'Can I use the same address twice?',
                  a: 'If you submit the same normalized address within 10 minutes, we return the existing quote request instead of creating a duplicate. After that window, you can create a new request for the same address if needed.',
                },
                {
                  q: 'How many bids do I get per request?',
                  a: 'Up to 3 bids per quote request. Once we have 3 bids (or the request expires), the request moves to READY and you can download the PDF or create the portal link.',
                },
                {
                  q: 'Is there a cost for realtors?',
                  a: 'Realtor accounts and quote requests are free. Your clients use CoreBids for the project and payments; you get full attribution for the referral.',
                },
              ].map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    style={{
                      background: '#ffffff',
                      border: `2px solid ${isOpen ? BRAND_GREEN : '#e2e8f0'}`,
                      borderRadius: 16,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isMobileLayout ? 20 : 24,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                      aria-expanded={isOpen}
                    >
                      <span style={{ fontSize: isMobileLayout ? 16 : 18, fontWeight: 600, color: '#0f172a', flex: 1, paddingRight: 16 }}>
                        {faq.q}
                      </span>
                      <span style={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, flexShrink: 0, minWidth: 24, textAlign: 'center' }}>
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: `0 ${isMobileLayout ? 20 : 24}px ${isMobileLayout ? 20 : 24}px`, fontSize: isMobileLayout ? 15 : 16, color: '#64748b', lineHeight: 1.6 }}>
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </ContentLimiter>
      </section>

      <section
        style={{
          padding: isMobileLayout ? '72px 16px' : '100px 40px',
          background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 100%)',
          textAlign: 'center',
        }}
      >
        <ContentLimiter>
          <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#fff', marginBottom: 24, lineHeight: 1.2 }}>
              Ready to Offer Roof Quotes to Your Clients?
            </h2>
            <p style={{ fontSize: isMobileLayout ? 18 : 20, color: 'rgba(255,255,255,0.9)', marginBottom: 40, lineHeight: 1.6 }}>
              Join Realtors already using CoreBids to close faster and look great doing it.
            </p>

            <button
              className="cb-realtor-btn-primary"
              onClick={handlePrimaryCta}
              style={{
                fontSize: isMobileLayout ? 18 : 20,
                fontWeight: 700,
                padding: isMobileLayout ? '16px 22px' : '20px 48px',
                backgroundColor: '#fff',
                color: BRAND_GREEN,
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 44,
              }}
            >
              Get Started Free <ArrowRight size={22} />
            </button>
          </div>
        </ContentLimiter>
      </section>
    </div>
  );
};

export default RealtorLanding;
// CB:END fr-014-realtor-landing
